import { API_BASE, getAccessToken } from "@/lib/api/session";

export const REFERRAL_STORAGE_KEY = "andrum.referral_code";

export type ReferralMe = {
  code: string;
  inviteUrl: string;
  rewardDaysPerInvite: number;
  invitedCount: number;
  rewardedWeeks: number;
  rewardDaysEarned: number;
  recent: Array<{
    status: string;
    rewardDays: number;
    rewardedAt: string | null;
  }>;
};

export function stashReferralCode(code: string | null | undefined): void {
  if (!code || typeof window === "undefined") return;
  const normalized = code.trim().toUpperCase();
  if (normalized.length < 4) return;
  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, normalized);
  } catch {
    // ignore
  }
}

export function readStashedReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearStashedReferralCode(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Capture ?ref= from current URL (and hash/search) into localStorage. */
export function captureReferralFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("ref") ?? params.get("invite");
    if (fromQuery) {
      stashReferralCode(fromQuery);
      return fromQuery.trim().toUpperCase();
    }
  } catch {
    // ignore
  }
  return readStashedReferralCode();
}

export async function fetchMyReferral(): Promise<ReferralMe | null> {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/api/v1/referrals/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as ReferralMe;
  } catch {
    return null;
  }
}

export async function claimReferralCode(
  code: string
): Promise<
  | { ok: true; rewardDays: number }
  | { ok: false; message: string; already?: boolean }
> {
  const token = await getAccessToken();
  if (!token) {
    return { ok: false, message: "Logga in för att använda inbjudan." };
  }
  try {
    const res = await fetch(`${API_BASE}/api/v1/referrals/claim`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      rewardDays?: number;
      message?: string;
      error?: string;
    };
    if (!res.ok) {
      return {
        ok: false,
        message: json.message ?? "Kunde inte använda inbjudan.",
        already: json.error === "ALREADY_REFERRED",
      };
    }
    return { ok: true, rewardDays: json.rewardDays ?? 7 };
  } catch {
    return { ok: false, message: "Nätverksfel vid inbjudan." };
  }
}

/** Claim stashed code once after signup; clears storage on success or already-used. */
export async function claimStashedReferralIfAny(): Promise<void> {
  const code = readStashedReferralCode();
  if (!code) return;
  const result = await claimReferralCode(code);
  if (result.ok || result.already) {
    clearStashedReferralCode();
  }
}
